const bundle = document.querySelector('.ved-bundle-section');
const vedMetadata = JSON.parse(document.getElementById('ved-metadata').textContent);

const expressAvgiftArtikel = vedMetadata.metadata.expressavgift_id;
const pantSackArtikel = vedMetadata.pantsack;

// Result from the last updateSummaryPrices run, shared with the buybox via getOrderPricing
let orderPricing = null;

const freeGiftEnabled = bundle.dataset.freeGiftEnable === 'true';
const freeGiftProduct = bundle.dataset.freeGiftProduct;

// Loading state for add to cart button (default and popup)
let isButtonLoading = false;

// Inventory cache for upsells
let upsellInventoryCache = {};
let popupUpsellInventoryCache = {};

// Steg och vyer
const steps = Array.from(bundle.querySelectorAll('.ved-bundle-step'));
const views = Array.from(bundle.querySelectorAll('.ved-bundle-view'));
const stepLinks = Array.from(bundle.querySelectorAll('.ved-bundle-step-link'));
let currentStep = 1;

let torkningLabels = {
  'torr_ved': 'Torr ved',
  'otorkad_ved': 'Otorkad ved'
};

// Has _short suffix for short labels
let forpackningLabels = {
  'avtippad': 'Avtippad ved',
  'avtippad_short': 'Avtippad',
  'storsack': 'Storsäck (2 m³/säck)',
  'storsack_short': 'Storsäck',
  'smasack': 'Småsäck (40 l/säck)',
  'smasack_short': 'Småsäck'
};

let state = {
  postnummerValid: false,
  leveransmanad: null,
  preorderdate: null,
  torkning: null,
  forpackning: null,
  produkt: null,
  variant: null,
  traslag: null,
  langd: null,
  antal: null,
  klyvning: null,
  valid: null,
  pantRetur: 0
};

// Separate state for the shared buybox module, since the module
// stores its own variant object which must not clobber state.variant
let buyboxState = {
  purchaseType: 'one_time',
  sellingPlan: null,
  variant: null
};

function findSellingPlanByName(plans, planName) {
  return plans?.find((plan) => plan.name === planName) ?? null;
}

// Net sack deposit after return offset; uses matching selling plan price when planName is set
function getPantNetAmount(planName = null) {
  const { sackCount, returOffset } = calculateSackPant();
  let pantPrice = 100;
  let amount = 0;

  if (sackCount > 0) {
    if (planName) {
      const plan = findSellingPlanByName(pantSackArtikel.selling_plans, planName);
      if (plan?.price) pantPrice = plan.price;
    }

    amount += pantPrice * sackCount;
  }

  if (returOffset > 0) {
    amount -= returOffset * pantPrice;
  }

  return amount;
}

// Sum upsell line items; planName picks subscription price when comparing selling plans
function getUpsellTotal(planName = null) {
  let amount = 0;

  for (const [variantId, count] of Object.entries(upsellInventoryCache)) {
    if (count <= 0) continue;

    const upsell = vedMetadata.tillbehor[variantId];
    if (!upsell) continue;

    if (planName) {
      const plan = findSellingPlanByName(upsell.selling_plans, planName);
      amount += (plan?.price ?? upsell.price) * count;
    } else {
      amount += upsell.price * count;
    }
  }

  return amount;
}

// Express fee only applies to immediate delivery
function getExpressFee() {
  return state.leveransmanad === 'now' ? vedMetadata.metadata.express_fee : 0;
}

// One-time product price before pant, upsells and express
function getOneTimeProductPrice() {
  if (state.torkning === 'torr_ved' && state.forpackning === 'smasack') {
    return state.variant?.price ?? 0;
  }

  const discountMultiplier = getBestDiscount();
  let subtotal = 0;

  if (state.torkning === 'otorkad_ved') {
    subtotal = (state.variant?.unit_price ?? 0) * (state.antal ?? 0);
  } else {
    subtotal = (state.produkt?.unit_price ?? 0) * (state.antal ?? 0);
  }

  return subtotal - subtotal * (discountMultiplier / 100);
}

// Selling plan product prices scaled to current bundle selection
function getSellingPlansPricing() {
  if (state.torkning === 'torr_ved' && state.forpackning === 'smasack') {
    return (state.variant?.selling_plans || []).map((plan) => ({
      id: plan.id,
      name: plan.name,
      price: plan.price,
    }));
  }

  const volumeFactor = 1 - getBestDiscount() / 100;
  const sourcePlans = state.torkning === 'otorkad_ved'
    ? state.variant?.selling_plans
    : state.produkt?.selling_plans;

  return (sourcePlans || []).map((plan) => ({
    id: plan.id,
    name: plan.name,
    price: plan.price * state.antal * volumeFactor,
  }));
}

// Attach matching selling plan to upsell lines when ordering a subscription
function createUpsellCartItem(variantId, count) {
  const item = {
    id: variantId,
    quantity: count
  };

  if (buyboxState.purchaseType === 'subscription' && buyboxState.sellingPlan) {
    const upsell = vedMetadata.tillbehor[variantId];
    const plan = findSellingPlanByName(upsell?.selling_plans, buyboxState.sellingPlan.name);

    if (plan) {
      item.selling_plan = plan.id;
    }
  }

  return item;
}

// Shared subscription buybox (vedkungen-buybox.js); null if markup or script is missing
const buybox = window.VedkungenBuybox?.create({
  state: buyboxState,
  onPriceChange: updateSummaryPrices,
  getOrderPricing: () => orderPricing,
});

buybox?.init();

// Build a variant-like object for the buybox based on the current selection.
// Volume discount applies to both one-time and subscription prices.
function getBuyboxVariantState() {

  // Smasack has pre-calculated prices per variant
  if (state.torkning === 'torr_ved' && state.forpackning === 'smasack') {
    return {
      id: state.variant.id,
      price: state.variant.price,
      selling_plans: state.variant.selling_plans || [],
    };

  // Storsack/Avtippad = product level selling plans
  } else if (state.torkning === 'torr_ved') {
    const discountMultiplier = getBestDiscount();
    const volumeFactor = 1 - discountMultiplier / 100;

    const selling_plans = (state.produkt.selling_plans || []).map((plan) => ({
      id: plan.id,
      name: plan.name,
      price: plan.price * state.antal * volumeFactor
    }));

    return {
      id: state.produkt.unit_id,
      price: state.produkt.unit_price * state.antal * volumeFactor,
      selling_plans
    };

  // Otorkad = variant level selling plans
  } else if (state.torkning === 'otorkad_ved') {
    const discountMultiplier = getBestDiscount();
    const volumeFactor = 1 - discountMultiplier / 100;

    const selling_plans = (state.variant.selling_plans || []).map((plan) => ({
      id: plan.id,
      name: plan.name,
      price: plan.price * state.antal * volumeFactor
    }));

    return {
      id: state.variant.id,
      price: state.variant.unit_price * state.antal * volumeFactor,
      selling_plans
    };
  }
}

// --- Steg-navigering ---

function updateStepHeader(currentStep) {
  steps.forEach((step) => {
    const stepNumber = parseInt(step.getAttribute("data-step"), 10);
    const checkmark = step.querySelector(".step-checkmark");

    if (stepNumber < currentStep) {
      // Mark previous steps as completed
      step.classList.add("completed");
      step.classList.remove("current");
      checkmark.classList.add("is-visible");
    } else if (stepNumber === currentStep) {
      // Highlight the current step
      step.classList.add("current");
      step.classList.remove("completed");
      checkmark.classList.remove("is-visible");
    } else {
      // Reset future steps
      step.classList.remove("completed", "current");
      checkmark.classList.remove("is-visible");
    }
  });

  // Hide announcement on step 2
  if (currentStep === 2) {
    bundle.querySelector('.ved-bundle-announcement').style.display = 'none';
  }

  syncStepLinkState();
}

function syncStepLinkState() {
  const currentView = views[currentStep - 1];
  const hasBackButton = Boolean(currentView?.querySelector('.ved-bundle-prev'));

  stepLinks.forEach((link) => {
    const targetStep = parseInt(link.getAttribute('data-step-target') || '', 10);
    const isBackward = Number.isFinite(targetStep) && targetStep < currentStep;
    const clickable = hasBackButton && isBackward;

    if (clickable) {
      link.classList.add('is-clickable');
      link.setAttribute('href', `#step-${targetStep}`);
      link.removeAttribute('aria-disabled');
      link.removeAttribute('tabindex');
      link.style.pointerEvents = '';
      link.style.cursor = 'pointer';
    } else {
      link.classList.remove('is-clickable');
      link.removeAttribute('href');
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');
      link.style.pointerEvents = 'none';
      link.style.cursor = 'default';
    }
  });
}

function showStep(step) {
  currentStep = step;

  views.forEach((view, i) => {
    view.style.display = (i + 1 === step) ? '' : 'none';
  });

  steps.forEach((s, i) => {
    s.classList.toggle('active', i + 1 === step);
  });

  updateStepHeader(currentStep);
}

bundle.addEventListener('click', function (e) {
  let validate = false;

  const stepLink = e.target.closest?.('.ved-bundle-step-link');

  // Allow going backwards by clicking the progress bar,
  // but only if the current step actually has a back button.
  if (stepLink) {
    e.preventDefault();

    const targetStepEl = stepLink.querySelector('.ved-bundle-step');
    const targetStep = parseInt(targetStepEl?.getAttribute('data-step') || '', 10);

    const currentView = views[currentStep - 1];
    const hasBackButton = Boolean(currentView?.querySelector('.ved-bundle-prev'));

    if (hasBackButton && Number.isFinite(targetStep) && targetStep < currentStep) {
      showStep(targetStep);
      validate = true;
      clearBundleError();
    }
  } else if (e.target.classList.contains('ved-bundle-next')) {
    e.preventDefault();

    if (validateStep(currentStep)) {
      showStep(currentStep + 1);
      validate = true;
    }
  } else if (e.target.classList.contains('ved-bundle-prev')) {
    e.preventDefault();
    showStep(currentStep - 1);
    validate = true;
    clearBundleError();
  }

  // Scroll to top on mobile if advancing and user has scrolled down
  if (validate) {
    if (window.scrollY > 100) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    updateStatusHeader();
  }
});

// --- Steg 1: Postnummer & leverans ---

// Postnummer input
const postnummerInput = bundle.querySelector('#postnummer');

// Only allow numbers in postnummer input
postnummerInput.addEventListener('input', function () {
  // Reset warning message
  bundle.querySelector('#postnummer-text').textContent = '';
  bundle.querySelector('#postnummer-notification').className = '';

  // Format / sanitize as XXX XX if we have more than 3 digits

  // Keep digits only, remove everything else (e, -, spaces, etc.)
  this.value = this.value.replace(/\D/g, "");

  // Limit to max 5 digits
  if (this.value.length > 5) {
    this.value = this.value.slice(0, 5);
  }

  // Format as XXX XX if we have more than 3 digits
  if (this.value.length > 3) {
    this.value = this.value.slice(0, 3) + " " + this.value.slice(3);
  }

  if (this.value.length === 6) {
    const isValid = validatePostnummer(this.value);
    if (isValid) this.blur();
  }
});

postnummerInput.addEventListener('keydown', function (e) {
  if (e.key === 'Enter') validatePostnummer(this.value);
});

function validatePostnummer(zipCode) {
  if (!zipCode) return;

  const postnummerNotification = bundle.querySelector('#postnummer-notification');
  const postnummerText = bundle.querySelector('#postnummer-text');

  const postnummerMin = Number(bundle.dataset.postnummerMin);
  const postnummerMax = Number(bundle.dataset.postnummerMax);

  const sanitizedInt = parseInt(zipCode.replace(/\s/g, ''), 10);
  const valid = /^\d{5}$/.test(sanitizedInt);

  // Reset messages
  postnummerNotification.className = '';
  postnummerText.innerHTML = '';

  if (valid) {
    const validatePostnummer = sanitizedInt >= postnummerMin && sanitizedInt <= postnummerMax;

    if (validatePostnummer) {
      postnummerNotification.classList.add('ved-bundle-notification');
      postnummerText.textContent = '✅ Du bor inom vårt upptagningsområde, vi kan leverera hem till dig.';
      state.postnummerValid = true;
      return true;
    } else {
      postnummerNotification.classList.add('ved-bundle-notification', '--red');
      postnummerText.innerHTML = '❌ Tyvärr, du bor utanför vårt upptagningsområde. Tror du att detta är fel? <a href="/pages/hjalp">Kontakta oss här</a> så hjälper vi dig!';
      state.postnummerValid = false;
    }
  } else {
    postnummerNotification.classList.add('ved-bundle-notification', '--red');
    postnummerText.textContent = '❌ Du angav ett ogiltigt postnummer. Vänligen ange ett giltigt postnummer (5 siffror).';
    state.postnummerValid = false;
  }

  return false;
}

// --- Steg 1: Torkning ---
bundle.querySelectorAll('.ved-bundle-option[data-name="torkning"]').forEach(btn => {
  btn.addEventListener('click', function () {
    bundle.querySelectorAll('.ved-bundle-option[data-name="torkning"]').forEach(b => {
      b.classList.remove('active');
    });

    this.classList.add('active');
    state.torkning = this.dataset.value;
    const torkningWarning = bundle.querySelector('.torkning-varning');
    const klyvningOptions = bundle.querySelector('#klyvning-options');

    if (this.dataset.value === 'otorkad_ved') {
      torkningWarning.style.display = '';
      klyvningOptions.style.display = '';
    } else {
      torkningWarning.style.display = 'none';
      klyvningOptions.style.display = 'none';

      if (state.klyvning) {
        bundle.querySelectorAll('.ved-bundle-option[data-name="klyvning"]').forEach(b => {
          b.classList.remove('active');
        });

        state.klyvning = null;
      }
    }

    // Rensa tidigare val träslag
    bundle.querySelector('#traslag-options').innerHTML = '';
    state.traslag = null;
    state.langd = null;
    state.produkt = null;
    updateStatusHeader();
  });
});

// --- Steg 1: Klyvning ---
bundle.querySelectorAll('.ved-bundle-option[data-name="klyvning"]').forEach(btn => {
  btn.addEventListener('click', function () {
    bundle.querySelectorAll('.ved-bundle-option[data-name="klyvning"]').forEach(b => {
      b.classList.remove('active');
    });

    this.classList.add('active');
    state.klyvning = this.dataset.value.charAt(0).toUpperCase() + this.dataset.value.slice(1);
    updateStatusHeader();
  });
});

// --- Steg 2: Förpackning (bildknappar, dynamiskt) ---
function renderForpackning() {
  const container = bundle.querySelector('#forpackning-options');
  let options = [];

  // Reset previous selections
  container.innerHTML = '';
  bundle.querySelector('.pant-notification').style.display = 'none';
  bundle.querySelector('#traslag-options').innerHTML = '';

  if (state.torkning === 'torr_ved') {
    options = [
      { label1: 'Avtippad', label2: 'Stjälpt mått', label3: '2 m³/säck', value: 'avtippad', img: 'forpackning_avtippad.webp' },
      { label1: 'Storsäck', label2: 'Stjälpt mått', label3: '2 m³/säck', value: 'storsack', img: 'forpackning_storsack.webp' },
      { label1: 'Småsäck', label2: 'Staplat mått', label3: '40 l/säck, 30 cm', value: 'smasack', img: 'forpackning_smasack.webp' },
    ];
  } else {
    options = [
      { label1: 'Avtippad', label2: 'Stjälpt mått', value: 'avtippad', img: 'forpackning_avtippad.webp' },
      { label1: 'Storsäck', label2: 'Stjälpt mått', label3: '2 m³/säck', value: 'storsack', img: 'forpackning_storsack.webp' },
    ];
  }

  options.forEach(opt => {
    const btn = document.createElement('button');

    btn.type = 'button';
    btn.className = 'ved-bundle-option';
    btn.dataset.name = 'forpackning';
    btn.dataset.value = opt.value;

    btn.innerHTML = `
      <img src="${window.Shopify?.routes?.root || '/'}cdn/shop/files/${opt.img}" alt="${opt.label1}" class="ved-bundle-option__img" loading="lazy">
      <span class="ved-bundle-option__label" style="font-weight: bold;">${opt.label1}</span>
      <span class="body-text">${opt.label2}</span>
      <span class="body-text">${opt.label3 ? opt.label3 : ''}</span>
    `;

    btn.addEventListener('click', function () {
      container.querySelectorAll('.ved-bundle-option').forEach(b => {
        b.classList.remove('active');
      });

      btn.classList.add('active');

      state.forpackning = opt.value;
      state.traslag = null;
      showAvailableVed();
      updateStatusHeader();
    });

    container.appendChild(btn);
  });
}

// Render förpackning när man går till steg 2
bundle.querySelector('.ved-bundle-next[data-next="2"]').addEventListener('click', renderForpackning);

function showAvailableVed() {
  const container = bundle.querySelector('#traslag-options');
  container.innerHTML = ''; // Rensa tidigare val

  const search = getMetadataKey();
  const searchVed = vedMetadata[search].slice().sort((a, b) => a.title.localeCompare(b.title));

  bundle.querySelector('.pant-notification').style.display = state.forpackning == 'storsack' ? '' : 'none';

  // For each ved, create button and get inventory using API
  for (const ved of searchVed) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ved-bundle-option ved-bundle-card';

    if (ved.inventory < 1) ved.valid = false;

    // If the product is not enabled, disable the button
    if (!ved.enabled) btn.disabled = true;

    btn.dataset.name = 'traslag';
    btn.dataset.value = ved.title;
    btn.dataset.valid = ved.valid;
    btn.dataset.vedid = ved.id;
    let fromPrice;

    if (state.forpackning === 'smasack') {
      fromPrice = formatMoney(ved.variants[0].unit_price);
    } else if (state.torkning === 'otorkad_ved') {
      fromPrice = formatMoney(ved.variants[0].unit_price);
    } else if (state.forpackning === 'storsack' || state.forpackning === 'avtippad') {
      fromPrice = formatMoney(ved.unit_price);
    }

    const unitLabel = state.torkning === 'torr_ved' ? '/säck' : '/m³';

    let btnHTML = `
          <div class="ved-bundle-card__img" style="background-image: url(${ved.image});">
              ${(!ved.valid) ? `<div class="ved-bundle-card__soldout body-text">Reservera</div>` : ''}
              <span class="body-text ved-bundle-card__badge">Från ${fromPrice} ${unitLabel}</span>
          </div>
          <span class="body-text ved-bundle-card__title">${ved.title}</span>
        `;

    if (ved.inventory < ved.inventory_varning) {
      btnHTML +=
        `<div class="ved-bundle-card__icon body-text" ${ved.valid ? 'style="color:orange;"' : ''}>
          <i class="material-icon outlined" data-icon="fiber_manual_record"></i>
          Låg lagerstatus
        </div>`;
    } else {
      btnHTML +=
        `<div class="ved-bundle-card__icon body-text" style="color:green;">
            <i class="material-icon outlined" data-icon="fiber_manual_record"></i>
            I lager
        </div>`;
    }

    btnHTML += `
      ${ved.length != null ? `
          <div class="ved-bundle-card__icon body-text">
              <i class="material-icon outlined" data-icon="straighten"></i>
              ${ved.length} cm
          </div>
      ` : ''}
      <div class="ved-bundle-card__icon body-text">
          <i class="material-icon outlined" data-icon="${ved.saljfordel_ikon}"></i>
          ${ved.saljfordel_text}
      </div>
      <span class="ved-bundle-card__info body-text">${ved.description}</span>
    `;

    btn.innerHTML = btnHTML;
    container.appendChild(btn);

    btn.addEventListener('click', function () {
      bundle.querySelectorAll('.ved-bundle-option[data-name="traslag"]').forEach(b => {
        b.classList.remove('active');
      });

      this.classList.add('active');
      state.traslag = this.dataset.value;
      state.langd = ved.length;
      state.valid = this.dataset.valid == 'true';

      const produktId = parseInt(this.dataset.vedid, 10);
      const search = getMetadataKey();
      state.produkt = vedMetadata[search].find(child => child.id === produktId);
      updateStatusHeader();
    });
  }
}

function renderSmasackVedQuantity() {
  const container = bundle.querySelector('#steg3-row');
  const lagerStatusWarning = bundle.querySelector('#lagerstatus-warning');
  lagerStatusWarning.style.display = 'none';

  // Find minimum and maximum bag_count among variants
  const bagCounts = state.produkt.variants.map(v => v.bag_count || 1);
  const min = Math.min(...bagCounts);
  let max = Math.max(...bagCounts);
  const defaultValue = min;

  // Low stock warning
  // Only applies if product is in stock
  if (state.produkt.inventory < max && state.valid) {
    max = state.produkt.inventory;
    lagerStatusWarning.style.display = 'block';
  }

  container.innerHTML = `
        <h3>Antal</h3>
        <label for="antal-slider">Antal säckar:</label>
        <input type="range" id="antal-slider" min="${min}" max="${max}" value="${defaultValue}" step="10">
        <span id="antal-value">${defaultValue} st</span>
    `;

  const antalSlider = container.querySelector('#antal-slider');
  const antalValue = container.querySelector('#antal-value');

  // Update antal and variant when slider is moved
  antalSlider.addEventListener('input', function () {
    state.antal = parseInt(this.value, 10);
    state.variant = state.produkt.variants.find(v => v.bag_count === state.antal);

    antalValue.textContent = `${this.value} st`;
    updatePricingLabelSmasack();
    updateStatusHeader();
  });

  state.antal = defaultValue;
  state.variant = state.produkt.variants[0];
  updatePricingLabelSmasack();
}

function updatePricingLabelSmasack() {
  let variant = state.produkt.variants.find(variant => variant.bag_count === state.antal);
  let rabatt = variant.save_amount;

  const summa = bundle.querySelector('.antal-summa');
  const prisInfo = bundle.querySelector('#pris-info');
  const sackInfo = bundle.querySelector('#sack-info');
  const rabattInfo = bundle.querySelector('#mangdrabatt-info');

  summa.style.display = '';
  prisInfo.innerHTML = `<strong>Pris:</strong> ${formatMoney(variant.price)}`;
  sackInfo.textContent = `(Säckpris: ${formatMoney(variant.unit_price)})`;

  if (rabatt > 0) {
    rabattInfo.textContent = `Du sparar ${formatMoney(rabatt)}`;
    rabattInfo.style.display = 'unset';
  } else {
    rabattInfo.style.display = 'none';
  }
}

// Storsack + torr_ved
function renderStorsackVedQuantity() {
  const container = bundle.querySelector('#steg3-row');
  const lagerStatusWarning = bundle.querySelector('#lagerstatus-warning');
  lagerStatusWarning.style.display = 'none';

  const min = 1;
  const defaultValue = min;
  let max = 12;

  // Low stock warning
  // Only applies if product is in stock
  if (state.produkt.inventory < max && state.valid) {
    max = state.produkt.inventory;
    lagerStatusWarning.style.display = 'block';
  }

  container.innerHTML = `
        <h3>Antal</h3>
        <label for="antal-slider">Antal säckar:</label>
        <input type="range" id="antal-slider" min="${min}" max="${max}" value="${defaultValue}" step="1">
        <span id="antal-value">${defaultValue} st</span>
    `;

  const antalSlider = container.querySelector('#antal-slider');
  const antalValue = container.querySelector('#antal-value');

  // Update antal and variant when slider is moved
  antalSlider.addEventListener('input', function () {
    state.antal = parseInt(this.value, 10);

    antalValue.textContent = `${this.value} st`;
    updatePricingLabelStorsack();
    updateStatusHeader();
  });

  state.antal = defaultValue;
  updatePricingLabelStorsack();
}

function updatePricingLabelStorsack() {
  const summa = bundle.querySelector('.antal-summa');
  const prisInfo = bundle.querySelector('#pris-info');
  const sackInfo = bundle.querySelector('#sack-info');
  const rabattInfo = bundle.querySelector('#mangdrabatt-info');

  let calculatedPrice = state.produkt.unit_price * state.antal;
  const discountMultiplier = getBestDiscount();
  const discount = calculatedPrice * (discountMultiplier / 100);
  calculatedPrice -= discount;

  summa.style.display = '';
  sackInfo.style.display = '';

  prisInfo.innerHTML = `<strong>Pris:</strong> ${formatMoney(calculatedPrice)}`;

  if (discount > 0) {
    rabattInfo.textContent = `Du sparar ${formatMoney(discount)}`;
    rabattInfo.style.display = 'unset';
  } else {
    rabattInfo.style.display = 'none';
  }
}

function renderOtorkadVedQuantity() {
  const container = bundle.querySelector('#steg3-row-2');
  const lagerStatusWarning = bundle.querySelector('#lagerstatus-warning');
  lagerStatusWarning.style.display = 'none';

  const min = 2;
  let max = 50;
  const step = 2;
  const defaultValue = min;

  // Låg lagerstatus varning
  // Only applicable when product is in stock
  if (state.produkt.inventory < max && state.valid) {
    max = state.produkt.inventory;
    lagerStatusWarning.style.display = 'block';
  }

  container.innerHTML = `
        <h3>Volym</h3>
        <label for="antal-slider">Volym:</label>
        <input type="range" id="antal-slider" min="${min}" max="${max}" value="${defaultValue}" step="${step}">
        <span id="antal-value">${defaultValue} m³</span>
    `;

  const antalSlider = container.querySelector('#antal-slider');
  const antalValue = container.querySelector('#antal-value');

  antalSlider.addEventListener('input', function () {
    state.antal = parseInt(this.value, 10);
    antalValue.textContent = `${this.value} m³`;
    updatePricingLabelOtorkad();
    updateStatusHeader();
  });

  state.antal = defaultValue;
}

function renderOtorkadVedLangd() {
  const container = bundle.querySelector('#steg3-row');

  const min = 25;
  let max = 50;
  const step = 5;
  const defaultValue = min;

  container.innerHTML = `
        <h3>Längd</h3>
        <label for="langd-slider">Längd:</label>
        <input type="range" id="langd-slider" min="${min}" max="${max}" value="${defaultValue}" step="${step}">
        <span id="langd-value">${defaultValue} cm</span>
    `;

  const langdSlider = container.querySelector('#langd-slider');
  const antalValue = container.querySelector('#langd-value');

  langdSlider.addEventListener('input', function () {
    state.langd = parseInt(this.value, 10);
    state.variant = state.produkt.variants.find(v => v.length === state.langd);

    antalValue.textContent = `${this.value} cm`;
    updatePricingLabelOtorkad();
    updateStatusHeader();
  });

  state.langd = defaultValue;
  state.variant = state.produkt.variants[0];
}

// Check if a chosen delivery month already qualifies as a reservation month
// (August-December in the current year, or any month in a later year)
function isValidReservationMonth(dateString) {
  if (!dateString || dateString === 'now' || dateString === 'none') return false;

  const parts = dateString.split('-');
  if (parts.length !== 2) return false;

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const currentYear = new Date().getFullYear();

  return year > currentYear || month >= 8;
}

// Pre-order select is only needed when sold out and the chosen month does not qualify
function needsPreorder() {
  return !state.valid && !isValidReservationMonth(state.leveransmanad);
}

// Render längd och antal när man går till steg 3
bundle.querySelector('.ved-bundle-next[data-next="3"]').addEventListener('click', function () {
  if (state.produkt === null) return;
  state.preorderdate = null; // reset pre-order date

  bundle.querySelector('#steg3-row').innerHTML = '';
  bundle.querySelector('#steg3-row-2').innerHTML = '';
  bundle.querySelector('#sack-info').textContent = '';

  bundle.querySelector('.ved-bundle-step3-preorder').style.display = needsPreorder() ? 'block' : 'none';

  if (state.forpackning === 'smasack') {
    renderSmasackVedQuantity();
  } else if (state.torkning === 'otorkad_ved') {
    renderOtorkadVedLangd();
    renderOtorkadVedQuantity();
    updatePricingLabelOtorkad();
  } else if (state.forpackning === 'storsack' || state.forpackning === 'avtippad') {
    renderStorsackVedQuantity();
  }
});

function updatePricingLabelOtorkad() {
  const summa = bundle.querySelector('.antal-summa');
  const prisInfo = bundle.querySelector('#pris-info');
  const sackInfo = bundle.querySelector('#sack-info');
  const rabattInfo = bundle.querySelector('#mangdrabatt-info');

  let calculatedPrice = state.variant.unit_price * state.antal;
  const discountMultiplier = getBestDiscount();
  const discount = calculatedPrice * (discountMultiplier / 100);
  calculatedPrice -= discount;

  summa.style.display = '';
  sackInfo.style.display = '';

  prisInfo.innerHTML = `<strong>Pris:</strong> ${formatMoney(calculatedPrice)}`;

  if (discount > 0) {
    rabattInfo.textContent = `Du sparar ${formatMoney(discount)}`;
    rabattInfo.style.display = 'unset';
  } else {
    rabattInfo.style.display = 'none';
  }
}

// Get best discount for the current antal
function getBestDiscount() {
  if (!state.produkt?.discount) return 0;

  const validDiscounts = state.produkt.discount.filter(d => d.minsta_antal <= state.antal);
  if (validDiscounts.length === 0) return 0;

  let bestDiscount = 0;
  let found = false;

  for (let d of validDiscounts) {
    if (d.rabatt_procent > bestDiscount) {
      found = true;
      bestDiscount = d.rabatt_procent;
    }
  }

  if (found) {
    return bestDiscount;
  } else {
    return 0;
  }
}

// --- Validering ---
function validateStep(step) {
  clearBundleError();

  if (step === 1) {
    if (!state.postnummerValid) {
      if (!validatePostnummer()) {
        showBundleError('Ange ett giltigt postnummer.');
        return false;
      }
    }

    state.leveransmanad = bundle.querySelector('#ved-leveransmanad-value').value;

    if (state.leveransmanad == "none") {
      showBundleError('Välj önskad leveransmånad.');
      return false;
    }

    if (!state.torkning) {
      showBundleError('Välj torkningsgrad.');
      return false;
    }

    if (state.torkning === 'otorkad_ved' && !state.klyvning) {
      showBundleError('Välj klyvning.');
      return false;
    }
  }

  if (step === 2) {
    if (!state.forpackning) {
      showBundleError('Välj förpackning.');
      return false;
    }

    if (!state.traslag) {
      showBundleError('Välj träslag.');
      return false;
    }
  }

  if (step === 3) {
    if (!state.langd && state.torkning === 'otorkad_ved') {
      showBundleError('Välj längd.');
      return false;
    }

    if (!state.antal) {
      showBundleError('Välj antal.');
      return false;
    }

    // Pre-order validation
    if (needsPreorder()) {
      const preorderDate = bundle.querySelector('#ved-preorder-value').value;

      if (preorderDate == 'none') {
        showBundleError('Välj leveransmånad.');
        return false;
      }
    }
  }

  clearBundleError();
  return true;
}

// Handle sack pant retur
document.getElementById('pantretur-slider').addEventListener('input', function () {
  let antalSackar = parseInt(this.value, 10);
  const pantLabel = bundle.querySelector('#pantretur-label');

  // Ensure value stays within bounds
  if (antalSackar < 0) this.value = 0;
  if (antalSackar > 12) this.value = 12;
  antalSackar = parseInt(this.value, 10);

  // Set values
  pantLabel.textContent = `${antalSackar} st`;
  state.pantRetur = antalSackar;
  updateSummaryPrices();
});

// Calculate full order pricing and update summary + buybox UI
function updateSummaryPrices() {
  if (!state.torkning || !state.forpackning) return null;

  const fraktItem = bundle.querySelector('#ved-bundle-fraktavgift');
  const pantItem = bundle.querySelector('#ved-bundle-pantavgift');
  const upsellItem = bundle.querySelector('#ved-bundle-merforsaljning');

  if (pantItem) pantItem.innerHTML = '';
  if (upsellItem) upsellItem.innerHTML = '';

  const expressFee = getExpressFee();
  const oneTimeProduct = getOneTimeProductPrice();
  const oneTimePant = getPantNetAmount();
  const oneTimeUpsells = getUpsellTotal();
  const oneTimeTotal = oneTimeProduct + oneTimePant + oneTimeUpsells + expressFee;

  // Build per-plan totals so buybox can compare one-time vs each subscription option
  const selling_plans = getSellingPlansPricing().map((plan) => {
    const pantAmount = getPantNetAmount(plan.name);
    const upsellAmount = getUpsellTotal(plan.name);
    const total = plan.price + pantAmount + upsellAmount + expressFee;

    return {
      id: plan.id,
      name: plan.name,
      price: plan.price,
      total,
    };
  });

  const isSubscription = buyboxState.purchaseType === 'subscription' && buyboxState.sellingPlan;
  const activePlanName = isSubscription ? buyboxState.sellingPlan.name : null;
  let totalPris = 0;
  let savedAmount = 0;

  if (isSubscription) {
    const planPricing = selling_plans.find((plan) => plan.id === buyboxState.sellingPlan.id);
    totalPris = planPricing?.total ?? buyboxState.sellingPlan.price;
    savedAmount = oneTimeTotal - totalPris;
  } else {
    totalPris = oneTimeTotal;

    if (state.torkning === 'torr_ved' && state.forpackning === 'smasack') {
      savedAmount = state.variant?.save_amount ?? 0;
    } else {
      const discountMultiplier = getBestDiscount();
      let subtotal = 0;

      if (state.torkning === 'otorkad_ved') {
        subtotal = (state.variant?.unit_price ?? 0) * (state.antal ?? 0);
      } else {
        subtotal = (state.produkt?.unit_price ?? 0) * (state.antal ?? 0);
      }

      savedAmount = subtotal * (discountMultiplier / 100);
    }
  }

  orderPricing = {
    oneTimeProduct,
    oneTimeTotal,
    expressFee,
    selling_plans,
    total: totalPris,
    savedAmount,
  };

  const discount = savedAmount > 0 ? `Du sparar: ${formatMoney(savedAmount)}` : '';

  if (fraktItem) {
    if (expressFee > 0) {
      fraktItem.textContent = `Expressleverans: ${formatMoney(expressFee)}`;
    } else {
      fraktItem.textContent = 'Frakt beräknas i kassan';
    }
  }

  const { sackCount, returOffset, showRetur } = calculateSackPant();
  let pantUnitPrice = 100;

  if (activePlanName) {
    const plan = findSellingPlanByName(pantSackArtikel.selling_plans, activePlanName);
    if (plan?.price) pantUnitPrice = plan.price;
  }

  if (pantItem) {
    if (sackCount > 0) {
      const pantAvgift = sackCount * pantUnitPrice;
      pantItem.innerHTML = `Säckpant (${sackCount} st): ${formatMoney(pantAvgift)}<br>`;
    }

    if (returOffset > 0) {
      const pantReturAvgift = returOffset * pantUnitPrice;
      pantItem.innerHTML += `Säckpant returavdrag (${returOffset} st): -${formatMoney(pantReturAvgift)}<br>`;
    }

    if (sackCount === 0 && returOffset === 0 && showRetur) {
      pantItem.innerHTML = `${state.pantRetur} säckar skall returneras<br>`;
    }
  }

  const upsellAmount = getUpsellTotal(activePlanName);

  if (upsellItem && upsellAmount > 0) {
    upsellItem.innerHTML = `Tillbehör: ${formatMoney(upsellAmount)}<br>`;
  }

  const totalprisEl = bundle.querySelector('#ved-bundle-totalpris');
  if (totalprisEl) totalprisEl.textContent = formatMoney(totalPris);

  const rabattEl = bundle.querySelector('#ved-bundle-rabatt');
  if (rabattEl) rabattEl.textContent = discount;

  const totalPriceEl = document.querySelector('#total-price');
  if (totalPriceEl) totalPriceEl.textContent = formatMoney(totalPris);

  buybox?.refreshPricing();

  return orderPricing;
}

// --- Steg 4: Summering ---
function renderSummary() {
  const summary = bundle.querySelector('#ved-bundle-summary');

  // If product is not valid, use pre-order date
  if (!state.valid) {
    if (needsPreorder()) {
      state.preorderdate = bundle.querySelector('#ved-preorder-value').value;
      state.leveransmanad = state.preorderdate;
    } else {
      // Chosen month already qualifies as a reservation month
      state.preorderdate = state.leveransmanad;
    }
  }

  summary.innerHTML = `
    <ul>
      <li><strong>Leverans:</strong> ${state.preorderdate ? `Reserverad för ${getDateLabel(state.preorderdate)}` : getDateLabel(state.leveransmanad)}</li>
      <li><strong>Torkning:</strong> ${torkningLabels[state.torkning]}</li>
      <li><strong>Träslag:</strong> ${state.traslag}</li>
      <li><strong>Förpackning:</strong> ${forpackningLabels[state.forpackning]}</li>
      <li>${getAmountLabel()}</li>
      ${state.klyvning ? `<li><strong>Klyvning:</strong> ${state.klyvning}` : ''}
    </ul>
  `;

  // Sync buybox with the current selection (updateSummaryPrices runs via onPriceChange)
  buybox?.update(getBuyboxVariantState());
}

bundle.querySelector('.ved-bundle-next[data-next="4"]').addEventListener('click', renderSummary);

// --- Lägg till i kundvagn ---
bundle.querySelectorAll('.button--add-to-cart').forEach((btn) => {
  btn.addEventListener('click', onBundleAddToCart);
});

function onBundleAddToCart(e) {
  e.preventDefault();

  // Return if loading
  if (isButtonLoading) return;

  // Set cart loading state
  setCartLoading(this, true);

  // Cart items object
  const cartItems = [];

  // Get main line item id based on torkning and forpackning
  let mainItemId;

  if (state.torkning === 'torr_ved' && state.forpackning === 'smasack') {
    mainItemId = state.variant.id;
  } else if (state.torkning === 'otorkad_ved') {
    mainItemId = state.variant.id;
  } else {
    // Storsack and avtippad ved uses produkt level, no variant level
    mainItemId = state.produkt.unit_id;
  }

  // Construct main line item
  const mainItem = {
    id: mainItemId,
    quantity: state.forpackning === 'smasack' ? 1 : state.antal, // Småsäck uses fixed quantity of 1, other forpacknings uses real quantity
    properties: { // called 'attributes' in Shopify
      'Förpackning': forpackningLabels[state.forpackning],
      'Leveransmånad': `${getDateLabel(state.leveransmanad)}`,

      // Used for invoice + weight calculation
      '_quantity_total': state.antal
    }
  };

  // Add selling plan for subscription purchases
  if (buyboxState.purchaseType === 'subscription' && buyboxState.sellingPlan) {
    mainItem.selling_plan = buyboxState.sellingPlan.id;
  }

  // Add additional properties for otorkad ved
  if (state.klyvning) {
    mainItem.properties['Klyvning'] = state.klyvning;
    mainItem.properties['Volym'] = `${state.antal} m³`;
  }

  // Add retur if applicable
  if (state.pantRetur > 0) {
    mainItem.properties['Storsäckar att returnera'] = `${state.pantRetur} st`;
  }

  cartItems.push(mainItem);

  // Add free gift if applicable
  if (freeGiftEnabled) {
    cartItems.push({
      id: freeGiftProduct,
      quantity: 1
    });
  }

  // Add express avgift if leverans is 'now'
  if (state.leveransmanad === 'now') {
    cartItems.push({
      id: expressAvgiftArtikel,
      quantity: 1
    });
  }

  // Add sack pant if applicable
  const { sackCount, returOffset } = calculateSackPant();

  if (sackCount > 0) {
    const pantItem = {
      id: pantSackArtikel.id,
      quantity: sackCount,
    };

    if (buyboxState.purchaseType === 'subscription' && buyboxState.sellingPlan) {
      const plan = findSellingPlanByName(pantSackArtikel.selling_plans, buyboxState.sellingPlan.name);

      if (plan) {
        pantItem.selling_plan = plan.id;
      }
    }

    cartItems.push(pantItem);
  }

  // Add upsell items
  if (Object.keys(upsellInventoryCache).length > 0) {
    for (const [variantId, count] of Object.entries(upsellInventoryCache)) {
      // Skip free gift product
      if (freeGiftEnabled && variantId === freeGiftProduct) continue;

      if (count > 0) {
        const upsellItem = createUpsellCartItem(variantId, count);
        cartItems.push(upsellItem);
      }
    }
  }

  // Get the discount code for pant return
  const discountCode = getReturnDiscountCode(returOffset);

  clearCartIfNotEmpty().then(() => {
    addCartAttributes().then(() => {
      addCartItems(cartItems).then(async success => {
        if (success) {
          if (discountCode) await addCartDiscount(discountCode);

          // If user already added upsell items, go to cart
          if (Object.keys(upsellInventoryCache).length > 0) {
            window.location.href = '/cart';
          } else {
            // Show upsell popup
            showUpsellPopup();
          }
        } else {
          alert('Något gick fel. Vänligen försök igen.');
          setCartLoading(this, false);
        }
      });
    });
  });
}

// Initiera första steget
showStep(1);

document.querySelectorAll('.ved-bundle-infobox-toggle').forEach(function (toggle) {
  toggle.addEventListener('click', function (e) {
    e.preventDefault();
    const infobox = this.parentElement.parentElement.querySelector('#infobox');
    if (infobox) {
      infobox.style.display = infobox.style.display === 'block' ? 'none' : 'block';
    }
  });
});

async function addCartItem(cartItem) {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cartItem)
  })
    .then(res => res.ok)
    .catch(() => false);
}

async function addCartItems(cartItems) {
  for (const cartItem of cartItems) {
    const success = await addCartItem(cartItem);
    if (!success) {
      return false;
    }
  }
  return true;
}

function getReturnDiscountCode(returOffset) {
  return returOffset > 0 ? `PANTRETUR_${returOffset}` : undefined;
}

function calculateSackPant() {
  let sackCount = 0;
  let returOffset = state.pantRetur;
  let showRetur = true;

  // Calculate how many sacks exist based on torkning and forpackning
  if (state.torkning === 'torr_ved' && state.forpackning === 'storsack') {
    sackCount = state.antal;
  } else if (state.torkning === 'otorkad_ved' && state.forpackning === 'storsack') {
    sackCount = Math.ceil(state.antal / 2);
  } else if (state.forpackning !== 'storsack') {
    sackCount = 0;
    showRetur = false;
  }

  // Substract any pant bags we are returning
  if (returOffset > 0) {

    // If we have more pant bags than we can return, minimize to available count
    const sackMinimized = Math.min(returOffset, sackCount);
    sackCount = Math.max(0, sackCount - returOffset);

    // Update to reflect how many bags we couldn't return
    returOffset = Math.max(0, returOffset - sackMinimized);
  }

  return { sackCount, returOffset, showRetur };
}

function showBundleError(msg) {
  const errorBox = bundle.querySelector('.ved-bundle-error');
  if (errorBox) {
    errorBox.textContent = msg;
    errorBox.style.display = '';
  }

  // Show native alert on mobile
  if (window.innerWidth < 768) {
    alert(msg);
  }
}

function clearBundleError() {
  const errorBox = bundle.querySelector('.ved-bundle-error');
  if (errorBox) {
    errorBox.textContent = '';
    errorBox.style.display = 'none';
  }
}

function formatMoney(amount) {
  amount = Math.round(Number(amount));
  return amount.toLocaleString('sv-SE') + ' kr';
}

function getDateLabel(dateString, showMoney) {
  if (dateString === 'now') {
    if (showMoney) {
      return `Omgående (+${formatMoney(getExpressFee())} expressavgift)`;
    } else {
      return 'Omgående';
    }
  } else {
    const date = new Date(dateString + '-01');
    const monthText = date.toLocaleString('sv-SE', { month: 'long' });
    const year = date.getFullYear();

    return `${monthText} ${year}`;
  }
}

function updateStatusHeader() {
  const groups = {
    1: [],
    2: [],
    3: [],
    4: [],
  };

  const push = (step, html) => {
    if (!html) return;
    groups[step].push(html);
  };

  // Step 1: leverans + torkning (+ klyvning)
  if (state.leveransmanad) {
    push(1, `<span class="body-text"><strong>Leverans:</strong> ${getDateLabel(state.leveransmanad, false)}</span>`);
  }
  if (state.torkning) {
    push(1, `<span class="body-text"><strong>Torkning:</strong> ${torkningLabels[state.torkning]}</span>`);
  }
  if (state.torkning === 'otorkad_ved' && state.klyvning) {
    push(1, `<span class="body-text"><strong>Klyvning:</strong> ${state.klyvning}</span>`);
  }

  // Step 2: förpackning + träslag
  if (state.forpackning) {
    push(2, `<span class="body-text"><strong>Förpackning:</strong> ${forpackningLabels[state.forpackning + '_short']}</span>`);
  }
  if (state.traslag) {
    push(2, `<span class="body-text"><strong>Träslag:</strong> ${state.traslag}</span>`);
  }

  // Step 3: längd + antal/volym
  if (state.langd) {
    push(3, `<span class="body-text"><strong>Längd:</strong> ${state.langd} cm</span>`);
  }
  if (state.antal) {
    push(3, `<span class="body-text">${getAmountLabel()}</span>`);
  }

  // Render items under each progress-bar step
  for (let step = 1; step <= 4; step++) {
    const container = bundle.querySelector(`.ved-bundle-step__body[data-status-step="${step}"]`);
    if (!container) continue;

    // Only show up to the current step (and never show on step 4, like before)
    if (currentStep === 4 || step > currentStep) {
      container.innerHTML = '';
      continue;
    }

    container.innerHTML = groups[step].join('');
  }
}

function getMetadataKey() {
  if (state.forpackning === 'storsack' && state.torkning === 'torr_ved') {
    return 'storsack';
  } else if (state.forpackning === 'smasack' && state.torkning === 'torr_ved') {
    return 'smasack';
  } else if (state.forpackning === 'avtippad' && state.torkning === 'torr_ved') {
    return 'storsack';
  } else if (state.torkning === 'otorkad_ved') {
    return 'otorkad';
  }

  return 'unknown';
}

function getAmountLabel() {
  if (state.torkning === 'otorkad_ved') {
    return `<strong>Volym:</strong> ${state.antal} m³`;
  } else if (state.forpackning === 'smasack') {
    return `<strong>Antal:</strong> ${state.antal} småsäckar`;
  } else if (state.forpackning === 'storsack' || state.forpackning === 'avtippad') {
    if (state.antal === 1) {
      return `<strong>Antal:</strong> 1 storsäck`;
    } else {
      return `<strong>Antal:</strong> ${state.antal} storsäckar`;
    }
  }

  return state.forpackning;
}

// Clear cart & discounts
async function clearCartIfNotEmpty() {
  const res = await fetch('/cart.js');
  const cart = await res.json();

  // Remove all discounts
  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ discount: '' })
  })
    .then(res => res.ok)
    .catch((error) => {
      console.error('[clearCartIfNotEmpty] Error removing discounts:', error);
    });

  // Clear cart
  if (cart.items.length > 0) {
    await fetch('/cart/clear.js', { method: 'POST' })
      .then(res => res.ok)
      .catch((error) => {
        console.error('[clearCartIfNotEmpty] Error clearing cart:', error);
      });
  }
}

// Add cart attributes
// TODO fix selling plan frequency
async function addCartAttributes() {
  let body = {
    attributes: {
      'leverans_manad': state.leveransmanad,
      'Leveransmånad': getDateLabel(state.leveransmanad),
      'Förpackning': forpackningLabels[state.forpackning],
      'Torkning': torkningLabels[state.torkning]
    }
  }

  // Add retur if applicable
  if (state.pantRetur > 0) {
    body.attributes['Storsäckar att returnera'] = `${state.pantRetur} st`;
  }

  // Add additional properties for otorkad ved
  if (state.klyvning) {
    body.attributes['Klyvning'] = state.klyvning;
    body.attributes['Volym'] = `${state.antal} m³`;
  }

  // Add subscription details if applicable
  // Used for client email notifications, server-side is handled by vedkungen-leveranssystem (don't trust client)
  if (buyboxState.purchaseType === 'subscription' && buyboxState.sellingPlan) {
    // TODO hardcoded, should be dynamic based on selling plan frequency
    const frequency = state.forpackning === 'smasack' ? 'month' : 'year';

    const { date: renewalDate, label: renewalDateLabel } = VedkungenBuybox.getRenewalDate(
      state.leveransmanad,
      frequency // TODO hardcoded, should be dynamic based on selling plan frequency
    );

    body.attributes['subscription_renewal_date'] = renewalDate;
    body.attributes['subscription_renewal_date_label'] = renewalDateLabel;
  }

  await fetch('/cart/update.js', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' }
  });
}

async function addCartDiscount(discountCode) {
  await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ discount: discountCode || '' })
  })
    .then(res => res.ok)
    .catch((error) => {
      console.error('[addCartDiscount] Error adding discount:', error);
    });
}

document.addEventListener('DOMContentLoaded', function () {
  const modal = document.getElementById('forpackning-modal');
  const openBtn = document.getElementById('forpackning-modal-btn');
  const closeBtn = modal.querySelector('.ved-bundle-modal__close');
  const overlay = modal.querySelector('.ved-bundle-modal__overlay');

  openBtn.addEventListener('click', () => {
    modal.style.display = 'flex';
  });
  closeBtn.addEventListener('click', () => {
    modal.style.display = 'none';
  });
  overlay.addEventListener('click', () => {
    modal.style.display = 'none';
  });
});

// Handle upsell quantity changes
document.addEventListener('change', (e) => {
  if (e.target.matches('.quantity__input')) {
    const variantId = parseInt(e.target.dataset.quantityVariantId, 10);
    const newQty = parseInt(e.target.value, 10);

    if (newQty > 0) {
      upsellInventoryCache[variantId] = newQty;
    } else {
      delete upsellInventoryCache[variantId];
    }

    updateSummaryPrices();
  }
});

// Set 'add to cart' loading state
function setCartLoading(btn, enabled) {
  isButtonLoading = enabled;

  if (enabled) {
    btn.classList.add('loading');
    btn.querySelector('.loading__spinner')?.classList.remove('hidden');
  } else {
    btn.classList.remove('loading');
    btn.querySelector('.loading__spinner')?.classList.add('hidden');
  }
}

// Show upsell popup on successful add to cart
function showUpsellPopup() {
  // Reset button loading state
  isButtonLoading = false;

  let modal = new Debutify.modal({
    closeMethods: ['overlay', 'button', 'escape'],
    size: 'medium',
    closeIcon: 'close',
    selector: '.vedkungen-upsell-popup',
    boxClass: [],
    overlayClass: [],
    onOpen: function () { },
    onClose: function () { }
  });
  modal.open();
  modal._busy(true); // Do not allow closing

  // Skip button event handlers
  const skipButtons = modal.modalBox.querySelectorAll('#popup-skip');

  skipButtons.forEach(button => {
    button.addEventListener('click', function () {
      const button = modal.modalBox.querySelector('.button--full-width');
      if (isButtonLoading) return;
      setCartLoading(button, true);

      window.location.href = '/cart';
    });
  });

  // Upsell component event handler
  modal.modalBox.querySelector('dbtfy-slider-component').addEventListener('change', function (e) {
    if (e.target.matches('.quantity__input')) {
      const variantId = parseInt(e.target.dataset.quantityVariantId, 10);
      const newQty = parseInt(e.target.value, 10);

      if (newQty > 0) {
        popupUpsellInventoryCache[variantId] = newQty;
      } else {
        delete popupUpsellInventoryCache[variantId];
      }
    }
  });

  // Checkout button event handler
  modal.modalBox.querySelector('.button--full-width').addEventListener('click', function (e) {
    e.preventDefault();

    if (isButtonLoading) return;
    setCartLoading(this, true);

    if (Object.keys(popupUpsellInventoryCache).length == 0) {
      window.location.href = '/cart';
    } else {
      const cartItems = [];

      for (const [variantId, count] of Object.entries(popupUpsellInventoryCache)) {
        if (count > 0) {
          const upsellItem = createUpsellCartItem(variantId, count);
          cartItems.push(upsellItem);
        }
      }

      addCartItems(cartItems).then(async success => {
        if (success) {
          window.location.href = '/cart';
        } else {
          alert('Något gick fel. Vänligen försök igen.');
          setCartLoading(this, false);
        }
      });
    }
  });
}
